import { Button } from "@mantine/core";
import { BuyButton } from "@src/components/plan/buy-button";
import { useCreateBillingPortalSession } from "@src/components/plan/mutations/create-billing-portal";
import { getPublicWebAppOrigin } from "@src/utils/app-url";
import { showErrorNotification } from "@src/utils/notifications";
import { AnimatePresence, motion } from "framer-motion";
import { useCallback } from "react";
import {
	PlanCard,
	PlanCta,
	PlanCtaButton,
	PlanDescription,
	PlanLabelBadge,
	PlanPrice,
} from "./plan-modal-card-ui";

export function ProPlanCard(props: {
	isPlanActive: boolean;
	isPlanCancelled: boolean;
	isButtonDisabled: boolean;
	periodSelected: "monthly" | "annually";
	proMonthlyPriceId: string | null;
	proAnnualPriceId: string | null;
	label: string;
}) {
	const {
		isPlanActive,
		isPlanCancelled,
		isButtonDisabled = false,
		periodSelected,
		proMonthlyPriceId,
		proAnnualPriceId,
		label,
	} = props;

	const { mutateAsync: createBillingPortalSession, isPending } =
		useCreateBillingPortalSession();

	const priceId =
		periodSelected === "annually" ? proAnnualPriceId : proMonthlyPriceId;

	const isManageMode = isPlanActive && !isPlanCancelled;
	const buttonIsDisabled = !isManageMode && (!priceId || isButtonDisabled);

	const handleManageClick = useCallback(async () => {
		try {
			const portalUrl = await createBillingPortalSession({
				previewReturnUrl: getPublicWebAppOrigin(),
			});
			if (portalUrl) {
				if (window.electronAPI?.openExternal) {
					await window.electronAPI.openExternal(portalUrl);
				} else {
					window.location.href = portalUrl;
				}
			} else {
				showErrorNotification(
					"Error",
					"Billing portal link was missing. Please try again.",
				);
			}
		} catch {
			// useCreateBillingPortalSession already reports via handleError in onError.
		}
	}, [createBillingPortalSession]);

	return (
		<PlanCard active={isPlanActive}>
			<PlanLabelBadge variant="pro">Pro</PlanLabelBadge>

			<AnimatePresence mode="wait">
				{periodSelected === "annually" ? (
					<motion.div
						key="annual"
						initial={{ opacity: 0 }}
						animate={{ opacity: 1 }}
						exit={{ opacity: 0 }}
						transition={{ duration: 0.15 }}
					>
						<PlanPrice price="$12" fullPrice="$20" period="monthly" />
					</motion.div>
				) : (
					<motion.div
						key="monthly"
						initial={{ opacity: 0 }}
						animate={{ opacity: 1 }}
						exit={{ opacity: 0 }}
						transition={{ duration: 0.15 }}
					>
						<PlanPrice price="$16" fullPrice="$25" period="monthly" />
					</motion.div>
				)}
			</AnimatePresence>

			<PlanDescription>
				<p>
					For professionals and small teams who need structured thinking, better
					AI, and a shared place to get work done.
				</p>
			</PlanDescription>

			<PlanCta>
				<PlanCtaButton>
					{isManageMode ? (
						<Button
							variant="cta"
							fullWidth
							loading={isPending}
							onClick={handleManageClick}
							title="Manage subscription and billing in Stripe"
						>
							{label}
						</Button>
					) : (
						<BuyButton
							priceId={priceId}
							variant="cta"
							disabled={buttonIsDisabled}
						>
							{label}
						</BuyButton>
					)}
				</PlanCtaButton>
			</PlanCta>
		</PlanCard>
	);
}
